import { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

const BRAND = {
  primary: "#2563eb",
  primarySoft: "rgba(37,99,235,0.08)",
  accent: "#1d4ed8",
  surface: "#f8fafc",
  border: "#e2e8f0",
  text: "#0f172a",
  muted: "#64748b",
  neutral: "#475569",
  success: "#16a34a",
  warning: "#d97706",
  error: "#dc2626",
};

type Ticket = {
  id: string;
  subject: string;
  customer: string;
  priority: "Low" | "Medium" | "High";
  channel: "App" | "Email";
  assignedTo?: string;
  createdAt: string;
  slaDue: string;
  status: "OPEN" | "IN_PROGRESS" | "RESOLVED";
};

type FAQ = {
  id: string;
  question: string;
  category: string;
  lastUpdated: string;
  status: "Draft" | "Published";
};

type Branch = {
  id: string;
  name: string;
  city: string;
  phone: string;
  isPrimary: boolean;
  mapsSync: "Synced" | "Pending";
};

const FAKE_TICKETS: Ticket[] = [
  {
    id: "T-2101",
    subject: "Delivery delayed beyond promised slot",
    customer: "Acme LLC",
    priority: "High",
    channel: "App",
    assignedTo: "Nadia Hussain",
    createdAt: "Today • 09:24",
    slaDue: "Due in 1h",
    status: "IN_PROGRESS",
  },
  {
    id: "T-2102",
    subject: "Need invoice copy for BK-10032",
    customer: "BuildCo",
    priority: "Medium",
    channel: "Email",
    assignedTo: "—",
    createdAt: "Today • 08:10",
    slaDue: "Due in 3h",
    status: "OPEN",
  },
  {
    id: "T-2103",
    subject: "Unable to upload delivery photos",
    customer: "Skyline",
    priority: "High",
    channel: "App",
    assignedTo: "Hamed Ali",
    createdAt: "Yesterday • 17:55",
    slaDue: "Breached",
    status: "OPEN",
  },
];

const AGENTS = ["Nadia Hussain", "Hamed Ali", "Peter Singh", "Unassigned"];

const FAQ_ITEMS: FAQ[] = [
  {
    id: "F-101",
    question: "How do I reschedule a delivery?",
    category: "Bookings",
    lastUpdated: "2d ago",
    status: "Published",
  },
  {
    id: "F-102",
    question: "What payment modes are supported?",
    category: "Finance",
    lastUpdated: "5d ago",
    status: "Published",
  },
  {
    id: "F-103",
    question: "Can I duplicate an RFQ?",
    category: "RFQ",
    lastUpdated: "Draft",
    status: "Draft",
  },
];

const BRANCHES: Branch[] = [
  {
    id: "B-01",
    name: "Head Office",
    city: "Dubai Marina",
    phone: "+971 4 123 4567",
    isPrimary: true,
    mapsSync: "Synced",
  },
  {
    id: "B-02",
    name: "Logistics Hub",
    city: "Jebel Ali",
    phone: "+971 4 765 4321",
    isPrimary: false,
    mapsSync: "Pending",
  },
  {
    id: "B-03",
    name: "Northern Branch",
    city: "Ras Al Khaimah",
    phone: "+971 7 245 7788",
    isPrimary: false,
    mapsSync: "Synced",
  },
];

const SupportPage = () => {
  const [tickets, setTickets] = useState<Ticket[]>(FAKE_TICKETS);
  const [assignMenu, setAssignMenu] = useState<{ anchorEl: HTMLElement | null; ticketId: string | null }>({
    anchorEl: null,
    ticketId: null,
  });
  const [faqItems] = useState<FAQ[]>(FAQ_ITEMS);
  const [branches] = useState<Branch[]>(BRANCHES);

  const openTickets = useMemo(() => tickets.filter((t) => t.status !== "RESOLVED"), [tickets]);

  const handleAssignClick = (ticketId: string) => (event: React.MouseEvent<HTMLElement>) => {
    setAssignMenu({ anchorEl: event.currentTarget, ticketId });
  };

  const handleAssignAgent = (agent: string) => {
    if (!assignMenu.ticketId) return;
    setTickets((prev) =>
      prev.map((ticket) => (ticket.id === assignMenu.ticketId ? { ...ticket, assignedTo: agent === "Unassigned" ? "—" : agent } : ticket))
    );
    setAssignMenu({ anchorEl: null, ticketId: null });
  };

  const closeAssignMenu = () => setAssignMenu({ anchorEl: null, ticketId: null });

  return (
    <>
      <div className="main_title">
        <h1>Support &amp; Helpdesk</h1>
        <p>Monitor tickets from the customer app, maintain knowledge base content, and manage branch metadata.</p>
      </div>

      <div className="cards">
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          {[
            { icon: <SupportAgentIcon sx={{ color: BRAND.primary }} />, label: "Tickets", value: openTickets.length, helper: "Open items awaiting response" },
            { icon: <ArticleOutlinedIcon sx={{ color: BRAND.primary }} />, label: "FAQs", value: faqItems.length, helper: "Entries visible inside the customer app" },
            { icon: <LocationOnOutlinedIcon sx={{ color: BRAND.primary }} />, label: "Branches", value: branches.length, helper: "Managed locations published to maps" },
          ].map((card) => (
            <Card
              key={card.label}
              sx={{
                flex: 1,
                borderRadius: 3,
                border: `1px solid ${BRAND.border}`,
                background: "linear-gradient(135deg, #ffffff, #f9fbff)",
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  {card.icon}
                  <Typography sx={{ fontWeight: 600, color: BRAND.neutral }}>{card.label}</Typography>
                </Stack>
                <Typography sx={{ fontSize: "2rem", fontWeight: 700, color: BRAND.text }}>{card.value}</Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: "0.9rem" }}>{card.helper}</Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {/* Tickets */}
        <Box sx={{ mb: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} mb={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.text }}>
                Open Tickets
              </Typography>
              <Typography sx={{ color: BRAND.muted, fontSize: "0.9rem" }}>
                Items raised via the customer app or email channels.
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
                background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.accent})`,
                boxShadow: "0 10px 25px rgba(37,99,235,0.2)",
              }}
            >
              New Ticket
            </Button>
          </Stack>

          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>SLA</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell>{ticket.id}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: "#0f172a" }}>{ticket.subject}</Typography>
                      <Typography sx={{ color: "#94a3b8", fontSize: "0.8rem" }}>{ticket.customer}</Typography>
                    </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={ticket.priority}
                      sx={{
                        fontWeight: 600,
                        backgroundColor:
                          ticket.priority === "High"
                            ? "rgba(220,38,38,0.12)"
                            : ticket.priority === "Medium"
                            ? "rgba(217,119,6,0.12)"
                            : "rgba(22,163,74,0.12)",
                        color:
                          ticket.priority === "High"
                            ? BRAND.error
                            : ticket.priority === "Medium"
                            ? BRAND.warning
                            : BRAND.success,
                      }}
                    />
                  </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: BRAND.primarySoft,
                            color: BRAND.accent,
                            fontSize: "0.8rem",
                          }}
                        >
                          {(ticket.assignedTo || "—").substring(0, 2).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 600, color: BRAND.text }}>{ticket.assignedTo || "—"}</Typography>
                          <Typography sx={{ color: "#94a3b8", fontSize: "0.75rem" }}>Support</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{ticket.channel}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ticket.slaDue}
                        sx={{
                          fontWeight: 600,
                          backgroundColor:
                            ticket.slaDue === "Breached"
                              ? "rgba(220,38,38,0.12)"
                              : ticket.slaDue.includes("Due")
                              ? "rgba(217,119,6,0.12)"
                              : BRAND.primarySoft,
                          color:
                            ticket.slaDue === "Breached"
                              ? BRAND.error
                              : ticket.slaDue.includes("Due")
                              ? BRAND.warning
                              : BRAND.accent,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={handleAssignClick(ticket.id)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Menu
            anchorEl={assignMenu.anchorEl}
            open={Boolean(assignMenu.anchorEl)}
            onClose={closeAssignMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {AGENTS.map((agent) => (
              <MenuItem key={agent} onClick={() => handleAssignAgent(agent)}>
                {agent}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* FAQ Management */}
        <Box sx={{ mb: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} mb={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.text }}>
                FAQ Management
              </Typography>
              <Typography sx={{ color: BRAND.muted, fontSize: "0.9rem" }}>
                Draft, edit, and push answers directly to the customer app.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
                borderColor: BRAND.primary,
                color: BRAND.primary,
                '&:hover': { borderColor: BRAND.accent, color: BRAND.accent },
              }}
            >
              Upload FAQ
            </Button>
          </Stack>

          <Grid container spacing={2.5}>
            {faqItems.map((faq) => (
              <Grid key={faq.id} item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, border: `1px solid ${BRAND.border}`, height: "100%" }}>
                  <CardContent>
                    <Chip
                      size="small"
                      label={faq.status}
                      sx={{
                        mb: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        backgroundColor: faq.status === "Published" ? "rgba(187,247,208,0.4)" : BRAND.primarySoft,
                        color: faq.status === "Published" ? BRAND.success : BRAND.accent,
                      }}
                    />
                    <Typography sx={{ fontWeight: 600, color: BRAND.text, mb: 1 }}>{faq.question}</Typography>
                    <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem", mb: 2 }}>
                      {faq.category} • Updated {faq.lastUpdated}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="text" sx={{ textTransform: "none" }}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          textTransform: "none",
                          borderRadius: 2,
                          backgroundColor: BRAND.primary,
                          '&:hover': { backgroundColor: BRAND.accent },
                        }}
                      >
                        Publish
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Branch Info */}
        <Box>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} mb={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.text }}>
                Branch Information
              </Typography>
              <Typography sx={{ color: BRAND.muted, fontSize: "0.9rem" }}>
                Control the list of offices synchronized with the Maps API.
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
                background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.accent})`,
              }}
            >
              Add Branch
            </Button>
          </Stack>

          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Branch</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>Maps Sync</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branches.map((branch) => (
                  <TableRow key={branch.id} hover>
                    <TableCell>{branch.name}</TableCell>
                    <TableCell>{branch.city}</TableCell>
                    <TableCell>{branch.phone}</TableCell>
                    <TableCell>
                      {branch.isPrimary ? (
                        <Chip label="Primary" size="small" sx={{ fontWeight: 600, backgroundColor: "rgba(16,185,129,0.15)", color: BRAND.success }} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={branch.mapsSync}
                        sx={{
                          fontWeight: 600,
                          backgroundColor: branch.mapsSync === "Synced" ? "rgba(16,185,129,0.15)" : "rgba(217,119,6,0.15)",
                          color: branch.mapsSync === "Synced" ? BRAND.success : BRAND.warning,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </div>
    </>
  );
};

export default SupportPage;

